import React from 'react';

interface PostPreviewProps {
  title: string;
  content: string;
  contentHtml?: string;
  author: {
    username: string;
    avatar: string;
  };
  category: string;
  categoryColor: string;
  categoryIcon: string;
  tags?: string[];
  images?: string[];
  metadata?: {
    isBreaking?: boolean;
    discussionType?: string;
    drama?: string;
    creatorName?: string;
    creatorRole?: string;
    rating?: number;
    storyType?: string;
    postType?: string;
    source?: string;
    mood?: string;
    tools?: string[];
    recommendedDramas?: string[];
    poll?: {
      options: Array<{ id: string; text: string }>;
      allowMultiple?: boolean;
      endDate?: string;
      showResults?: boolean;
    };
  };
  onBack: () => void;
  onSubmit: () => void;
  loading?: boolean;
  submitButtonText?: string;
}

export default function PostPreview({
  title,
  content,
  contentHtml,
  author,
  category,
  categoryColor,
  categoryIcon,
  tags = [],
  images = [],
  metadata = {},
  onBack,
  onSubmit,
  loading = false,
  submitButtonText = 'Publish Post'
}: PostPreviewProps) {
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i key={i} className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400`}></i>
    ));
  };

  const storyTypeLabels = {
    filming: 'Filming Story',
    production: 'Production Insight',
    cast: 'Cast & Crew',
    technical: 'Technical Details',
    other: 'Other'
  };

  const postTypeLabels = {
    showcase: 'Showcase',
    tutorial: 'Tutorial',
    discussion: 'Discussion',
    question: 'Question',
    review: 'Tool Review'
  };

  const roleLabels = {
    actor: 'Actor/Actress',
    director: 'Director',
    writer: 'Writer',
    producer: 'Producer',
    composer: 'Composer',
    other: 'Other'
  };

  const discussionTypeLabels = {
    discussion: { label: 'Discussion', icon: 'ri-discuss-line' },
    question: { label: 'Question', icon: 'ri-question-line' },
    opinion: { label: 'Opinion', icon: 'ri-chat-quote-line' },
    poll: { label: 'Poll', icon: 'ri-bar-chart-horizontal-line' },
    announcement: { label: 'Announcement', icon: 'ri-megaphone-line' }
  };

  const moodEmojis = {
    excited: '🎉',
    curious: '🤔',
    thoughtful: '💭',
    happy: '😊',
    nostalgic: '🥹'
  };

  const isPoll = metadata.discussionType === 'poll' && metadata.poll;

  return (
    <div className="space-y-6">
      {/* Preview Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <i className="ri-eye-line text-gray-500"></i>
          <span className="text-sm font-medium text-gray-700">
            {isPoll ? 'Poll Preview' : 'Post Preview'}
          </span>
        </div>
        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
          This is how your {isPoll ? 'poll' : 'post'} will appear
        </span>
      </div>

      {/* Preview Card */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Category Badge */}
        <div className={`${categoryColor} px-4 py-2 flex items-center space-x-2`}>
          <i className={`${categoryIcon} text-white`}></i>
          <span className="text-white text-sm font-medium">{category}</span>
          {metadata.isBreaking && (
            <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full ml-auto">
              <i className="ri-alarm-warning-line mr-1"></i>Breaking News
            </span>
          )}
          {isPoll && (
            <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full ml-auto">
              <i className="ri-bar-chart-horizontal-line mr-1"></i>Poll
            </span>
          )}
        </div>

        <div className="p-6">
          {/* Author Info */}
          <div className="flex items-center space-x-3 mb-4">
            <img
              src={author.avatar}
              alt={author.username}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="flex items-center flex-wrap gap-2">
              <span className="font-medium text-gray-900">{author.username}</span>
              <span className="text-gray-500 text-sm">• Just now</span>
              {metadata.mood && moodEmojis[metadata.mood] && (
                <span className="text-sm" title={metadata.mood}>
                  {moodEmojis[metadata.mood]}
                </span>
              )}
            </div>
          </div>

          {/* Metadata Section */}
          {(metadata.drama ||
            metadata.creatorName ||
            metadata.rating ||
            metadata.storyType ||
            metadata.postType ||
            metadata.discussionType) && (
            <div className="flex flex-wrap items-center gap-3 mb-3 text-sm">
              {metadata.discussionType &&
                discussionTypeLabels[metadata.discussionType] && (
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                    <i
                      className={discussionTypeLabels[metadata.discussionType].icon}
                    ></i>
                    {discussionTypeLabels[metadata.discussionType].label}
                  </span>
                )}
              {metadata.drama && (
                <span className="text-gray-700">
                  <i className="ri-film-line mr-1"></i>
                  {metadata.drama}
                </span>
              )}
              {metadata.creatorName && (
                <span className="text-gray-700">
                  <i className="ri-user-star-line mr-1"></i>
                  {metadata.creatorName}
                  {metadata.creatorRole && (
                    <span className="text-gray-500 ml-1">
                      (
                      {roleLabels[metadata.creatorRole] ||
                        metadata.creatorRole}
                      )
                    </span>
                  )}
                </span>
              )}
              {metadata.rating && (
                <div className="flex items-center space-x-1">
                  {renderStars(metadata.rating)}
                  <span className="text-gray-600 ml-1">
                    ({metadata.rating}/5)
                  </span>
                </div>
              )}
              {metadata.storyType && (
                <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">
                  {storyTypeLabels[metadata.storyType] ||
                    metadata.storyType}
                </span>
              )}
              {metadata.postType && (
                <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs">
                  {postTypeLabels[metadata.postType] || metadata.postType}
                </span>
              )}
              {metadata.source && (
                <span className="text-gray-500 text-xs">
                  <i className="ri-links-line mr-1"></i>
                  Source: {metadata.source}
                </span>
              )}
            </div>
          )}

          {/* Title */}
          <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>

          {/* Content */}
          {content && (
            <div
              className="text-gray-700 prose prose-sm max-w-none mb-4"
              dangerouslySetInnerHTML={{
                __html: contentHtml || content
              }}
            />
          )}

          {/* Poll Options Preview */}
          {isPoll && metadata.poll && (
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 mb-4 border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                  <i className="ri-bar-chart-horizontal-line"></i>
                  Vote on this poll
                </h4>
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  {metadata.poll.allowMultiple && (
                    <span className="bg-blue-100 px-2 py-0.5 rounded">
                      <i className="ri-checkbox-multiple-line mr-1"></i>
                      Multiple choice
                    </span>
                  )}
                  {metadata.poll.endDate && (
                    <span className="bg-blue-100 px-2 py-0.5 rounded">
                      <i className="ri-time-line mr-1"></i>
                      Ends{' '}
                      {new Date(metadata.poll.endDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                {metadata.poll.options.map((option, index) => (
                  <div
                    key={option.id}
                    className="relative bg-white rounded-lg border-2 border-blue-200 hover:border-blue-400 transition-all cursor-pointer group"
                  >
                    <div className="flex items-center p-3">
                      <div
                        className={`w-5 h-5 ${
                          metadata.poll?.allowMultiple
                            ? 'rounded'
                            : 'rounded-full'
                        } border-2 border-blue-300 mr-3 flex items-center justify-center group-hover:border-blue-500 transition-colors`}
                      >
                        {index === 0 && (
                          <div
                            className={`w-2.5 h-2.5 ${
                              metadata.poll?.allowMultiple
                                ? 'rounded-sm'
                                : 'rounded-full'
                            } bg-blue-500`}
                          ></div>
                        )}
                      </div>
                      <span className="text-gray-800 font-medium">
                        {option.text}
                      </span>
                    </div>

                    {/* Preview vote bar (showing first option as selected) */}
                    {index === 0 && metadata.poll?.showResults && (
                      <div
                        className="absolute bottom-0 left-0 h-1 bg-blue-400 rounded-b-lg"
                        style={{ width: '100%' }}
                      ></div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                <span>
                  <i className="ri-group-line mr-1"></i>
                  0 votes
                </span>
                {!metadata.poll.showResults && (
                  <span className="text-blue-600">
                    <i className="ri-eye-off-line mr-1"></i>
                    Results hidden until you vote
                  </span>
                )}
              </div>
            </div>
          )}

          {/* AI Tools Used */}
          {metadata.tools && metadata.tools.length > 0 && (
            <div className="bg-orange-50 rounded-lg p-3 mb-4">
              <h4 className="text-sm font-medium text-orange-800 mb-2">
                <i className="ri-tools-line mr-1"></i>AI Tools Used:
              </h4>
              <div className="flex flex-wrap gap-2">
                {metadata.tools.map((tool, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-white text-orange-700 text-xs rounded border border-orange-200"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Dramas */}
          {metadata.recommendedDramas &&
            metadata.recommendedDramas.length > 0 && (
              <div className="bg-green-50 rounded-lg p-3 mb-4">
                <h4 className="text-sm font-medium text-green-800 mb-2">
                  <i className="ri-movie-2-line mr-1"></i>Recommended Dramas:
                </h4>
                <div className="flex flex-wrap gap-2">
                  {metadata.recommendedDramas.map((drama, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-white text-green-700 text-xs rounded border border-green-200"
                    >
                      {drama}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Images */}
          {images.length > 0 && (
            <div className="mb-4">
              <div
                className={`grid gap-2 ${
                  images.length === 1
                    ? 'grid-cols-1'
                    : images.length === 2
                    ? 'grid-cols-2'
                    : 'grid-cols-3'
                }`}
              >
                {images.map((img, index) => (
                  <div
                    key={index}
                    className="relative aspect-video rounded-lg overflow-hidden bg-gray-100"
                  >
                    <img
                      src={img}
                      alt={`Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className={`px-2 py-1 ${categoryColor}/10 text-gray-700 text-xs rounded-full`}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Interaction Preview */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-sm text-gray-500">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i>
                <span>0</span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-message-3-line"></i>
                <span>0</span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                <span>0</span>
              </span>
            </div>
            <span className="flex items-center gap-1">
              <i className="ri-share-line"></i>
            </span>
          </div>
        </div>
      </div>

      {/* Moderation Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
        <i className="ri-information-line text-amber-500 text-lg mt-0.5"></i>
        <div>
          <p className="text-sm text-amber-800 font-medium">
            Moderation Notice
          </p>
          <p className="text-xs text-amber-700 mt-1">
            Your {isPoll ? 'poll' : 'post'} will be reviewed by our moderation
            team before being published. This usually takes a few minutes.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Edit {isPoll ? 'Poll' : 'Post'}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={loading}
          className={`${categoryColor} text-white px-6 py-2 rounded-lg hover:opacity-90 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50`}
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              {isPoll ? 'Creating Poll...' : 'Publishing...'}
            </>
          ) : (
            <>
              <i
                className={`${
                  isPoll ? 'ri-bar-chart-horizontal-line' : 'ri-send-plane-line'
                } mr-2`}
              ></i>
              {submitButtonText}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
